import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
    // Setup axios interceptor for auto-refresh on 401
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        if (
          error.response?.status === 401 &&
          !originalRequest._retry &&
          !originalRequest.url.includes('/auth/login') &&
          !originalRequest.url.includes('/auth/refresh')
        ) {
          originalRequest._retry = true;
          try {
            const { data } = await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
            if (data?.token) {
              localStorage.setItem('dt_admin_token', data.token);
              originalRequest.headers['Authorization'] = `Bearer ${data.token}`;
              return axios(originalRequest);
            }
          } catch (refreshErr) {
            localStorage.removeItem('dt_admin_token');
            setUser(false);
          }
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('dt_admin_token');
    if (!token) {
      setUser(false);
      setLoading(false);
      return;
    }
    try {
      const { data } = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true,
      });
      setUser(data);
    } catch (e) {
      // Try refresh
      try {
        const { data: refreshed } = await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
        if (refreshed?.token) {
          localStorage.setItem('dt_admin_token', refreshed.token);
          const { data: me } = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${refreshed.token}` },
            withCredentials: true,
          });
          setUser(me);
        } else {
          throw new Error('No token from refresh');
        }
      } catch (refreshErr) {
        localStorage.removeItem('dt_admin_token');
        setUser(false);
      }
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const { data } = await axios.post(
      `${API}/auth/login`,
      { email, password },
      { withCredentials: true }
    );
    localStorage.setItem('dt_admin_token', data.token);
    setUser(data);
    return data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      // Logout chamada falhou - servidor inacessível ou sessão já expirada.
      // Continuamos limpando o estado local mesmo assim.
      if (process.env.NODE_ENV !== 'production') {
        console.warn('Logout API call failed (clearing local state anyway):', error?.message);
      }
    }
    localStorage.removeItem('dt_admin_token');
    setUser(false);
  };

  const getAuthHeader = () => {
    const token = localStorage.getItem('dt_admin_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, getAuthHeader }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

export function formatApiError(detail) {
  if (detail == null) return 'Algo deu errado. Tente novamente.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).join(' ');
  }
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}
